import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@hooks/useTheme";
import CustomSelect from "@components/UI/CustomSelect";
import PaginationEllipsis from "./PaginationEllipsis";

// Shared pagination bar: go-to-page input, items-per-page selector,
// "showing X - Y of Z" counter and the page buttons.
// `onItemsPerPageChange` receives the selected number of rows.
const PaginationComponent = ({
  currentPage,
  setCurrentPage,
  totalPages,
  totalData,
  itemsPerPage,
  onItemsPerPageChange,
  shouldShowGoToPage,
  showPagination,
  showItemsPerPageSelector,
  useFixedSelect,
  data,
  dataTestId,
}) => {
  const { colors } = useTheme();
  const { t, i18n } = useTranslation();

  const [pageInput, setPageInput] = useState(String(currentPage));

  useEffect(() => {
    setPageInput(String(currentPage));
  }, [currentPage]);

  const commitPage = () => {
    if (pageInput === "") {
      setCurrentPage(1);
      setPageInput("1");
      return;
    }

    let page = Number(pageInput);

    if (!Number.isInteger(page)) {
      setPageInput(String(currentPage));
      return;
    }

    if (page < 1) page = 1;
    if (page > totalPages) page = totalPages;

    setCurrentPage(page);
    setPageInput(String(page));
  };

  const handlePrevious = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNext = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  const getPaginationRange = () => {
    const pages = [];
    const maxPagesToShow = 3;

    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
      return pages;
    }

    const delta = Math.floor(maxPagesToShow / 2);
    let startPage = Math.max(1, currentPage - delta);
    let endPage = startPage + maxPagesToShow - 1;

    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = totalPages - maxPagesToShow + 1;
    }

    if (startPage > 1) {
      pages.push("...");
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    if (endPage < totalPages) {
      pages.push("...");
    }

    return pages;
  };

  const itemsPerPageOptions = [
    { value: 10, label: "10" },
    { value: 20, label: "20" },
    { value: 50, label: "50" },
  ];

  if (!showPagination || data?.length === 0) return null;

  const paginationTestId = dataTestId ? `${dataTestId}-pagination` : undefined;

  return (
    <div className="pt-3 flex-shrink-0" data-testid={paginationTestId}>
      <div className="flex flex-row items-center justify-between gap-x-0.5 gap-y-4 flex-wrap">
        {/* Go to page */}
        {shouldShowGoToPage && (
          <div
            className="flex items-center gap-1 text-sm flex-nowrap justify-center lg:justify-start min-w-0"
            data-testid={dataTestId ? `${dataTestId}-pagination-controls` : undefined}
          >
            <span className={`${colors.textSubTitle} text-xs font-medium inline`}>{t("common.table.go-to-page")}</span>
            <input
              type="number"
              min="1"
              max={totalPages}
              data-testid={dataTestId ? `${dataTestId}-pagination-go-to-page` : undefined}
              value={pageInput}
              onChange={(e) => {
                setPageInput(e.target.value);
              }}
              onBlur={commitPage}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  commitPage();
                  e.target.blur();
                }
              }}
              className={`h-8 w-10 rounded border px-2 py-1 text-sm font-medium focus:outline-none ${colors.bgInput} ${colors.borderPrimary} ${colors.textPrimary} ${colors.borderHoverPrimary}`}
            />

            {/* Items per page selector */}
            {showItemsPerPageSelector && (
              <div
                className="flex items-center gap-1 text-sm"
                data-testid={dataTestId ? `${dataTestId}-pagination-items-per-page` : undefined}
              >
                <span className={`${colors.textSubTitle} text-xs font-medium break-words`}>{t("common.table.items-per-page")}</span>
                <CustomSelect
                  data-testid={dataTestId ? `${dataTestId}-pagination-items-per-page-select` : undefined}
                  value={itemsPerPageOptions.find(option => option.value === itemsPerPage)}
                  onChange={(selectedOption) => onItemsPerPageChange(parseInt(selectedOption.value))}
                  options={itemsPerPageOptions}
                  size="sm"
                  isSearchable={false}
                  clearable={false}
                  className="w-16 text-sm font-medium"
                  menuPosition={useFixedSelect ? "fixed" : "absolute"}
                  customStyles={{
                    control: (base) => ({
                      ...base,
                      minWidth: "60px",
                      width: "60px",
                    }),
                    menu: (base) => ({
                      ...base,
                      width: "60px",
                      zIndex: 9999,
                    })
                  }}
                />
              </div>
            )}
            {/* Showing info - tabular-nums for consistent digit width, whitespace-nowrap to prevent line drop */}
            <div className="flex items-center text-xs min-w-0">
              <span className={`${colors.textSubTitle} text-xs font-medium break-words tabular-nums`}>
                {(() => {
                  const startItem = ((currentPage - 1) * itemsPerPage) + 1;
                  const endItem = Math.min(currentPage * itemsPerPage, totalData);
                  const currentLanguage = i18n.language || 'ko';

                  if (currentLanguage === 'ko') {
                    return (
                      <span className="text-xs tabular-nums">
                        총 <span className={colors.textPrimary}>{totalData}</span>개 중 <span className={colors.textPrimary}>{startItem} - {endItem}</span>개 표시 중
                      </span>
                    );
                  } else {
                    return (
                      <span className="text-xs tabular-nums">
                        {t("common.table.showing")} <span className={colors.textPrimary}>{startItem} - {endItem}</span> {t("common.table.of")} <span className={colors.textPrimary}>{totalData}</span>
                      </span>
                    );
                  }
                })()}
              </span>
            </div>
          </div>
        )}

        {/* Row 2: Pagination Controls */}
        {totalPages > 1 && (
          <div
            className="flex items-center gap-1 flex-nowrap justify-center shrink-0"
            data-testid={dataTestId ? `${dataTestId}-pagination-pages` : undefined}
          >
            {/* Previous Page Button */}
            <button
              type="button"
              data-testid={dataTestId ? `${dataTestId}-pagination-prev` : undefined}
              onClick={handlePrevious}
              disabled={currentPage === 1}
              className={`flex items-center gap-1 pr-2 pb-2 pt-2 text-sm ${colors.textActive} transition-colors disabled:cursor-not-allowed disabled:opacity-30`}
              title="Previous page"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
              </svg>
              <span className={`${colors.textActive} text-xs font-medium`}>{t("common.table.prev")}</span>
            </button>

            {/* Page Numbers */}
            <div className="flex items-center gap-1">
              {getPaginationRange().map((page, index) => (
                <div key={index}>
                  {page === "..." ? (
                    <PaginationEllipsis
                      dataTestId={dataTestId ? `${dataTestId}-pagination-ellipsis-${index}` : undefined}
                      className={`inline-flex items-center justify-center rounded border px-3 py-1 text-sm ${colors.borderPaginationButton} ${colors.textPrimary} ${colors.bgInput}`}
                    />
                  ) : (
                    <button
                      type="button"
                      data-testid={dataTestId ? `${dataTestId}-pagination-page-${page}` : undefined}
                      onClick={() => setCurrentPage(page)}
                      className={`rounded border px-3 py-1 text-sm transition-colors ${colors.borderPaginationButton} ${colors.textPrimary} ${page === currentPage ? `${colors.bgPaginationButtonActive}` : `${colors.bgInput}`
                        }`}
                    >
                      {page}
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Next Page Button */}
            <button
              type="button"
              data-testid={dataTestId ? `${dataTestId}-pagination-next` : undefined}
              onClick={handleNext}
              disabled={currentPage === totalPages}
              className={`flex items-center gap-1 pl-2 pb-2 pt-2 text-sm ${colors.textActive} transition-colors disabled:cursor-not-allowed disabled:opacity-50`}
              title="Next page"
            >
              <span className={`${colors.textActive} text-xs font-medium`}>{t("common.table.next")}</span>
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaginationComponent;
